import React, { useState } from 'react';
import { useDrop } from 'react-dnd';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Box, Paper, Typography, Divider, Chip } from '@mui/material';
import FlightTile from './FlightTile';
import BeltTimeDialog from './BeltTimeDialog';
import { usePiers } from '../context/PiersContext';
import { computeBeltGroups } from '../utils/beltUtils';

const BeltColumn = ({ beltNumber, flights = [], onToggleClosed, schedule, configuration, groupInfo, podColor, podName, searchTerm }) => {
  const { moveFlight } = usePiers();

  // Check if a flight's destination routing matches this belt's pod
  const isCrossPodDrop = (item) => {
    const destGroups = configuration.destinationBeltGroups;
    if (!destGroups || !item?.destinations?.length) return false;
    const firstDest = item.destinations[0];
    // Find which pod this flight should be in
    let correctPod = null;
    for (const group of destGroups) {
      if (group.destinations && group.destinations.includes(firstDest)) {
        correctPod = group.beltRange;
        break;
      }
    }
    // Default pod (destinations: null) if no match
    if (!correctPod) {
      const defaultGroup = destGroups.find(g => g.destinations === null);
      if (defaultGroup) correctPod = defaultGroup.beltRange;
    }
    if (!correctPod) return false;
    // Is this belt outside the correct pod?
    return beltNumber < correctPod[0] || beltNumber > correctPod[1];
  };

  const [{ isOver, dragItem }, drop] = useDrop({
    accept: 'flight',
    drop: (item) => {
      moveFlight({ flightNum: item.flightNum, depTime: item.depTime }, item.fromBelt, beltNumber);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      dragItem: monitor.getItem(),
    }),
    canDrop: () => true,
  });

  const crossPodWarning = isOver && isCrossPodDrop(dragItem);

  const isFirstInPair = groupInfo?.isFirst && groupInfo?.groupSize > 1;
  const isSecondInPair = groupInfo?.isLast && groupInfo?.groupSize > 1;

  return (
    <Paper
      ref={drop}
      sx={{
        px: 1.5,
        py: 1,
        width: 280,
        height: '100%',
        bgcolor: crossPodWarning
          ? 'rgba(255, 0, 0, 0.25)'
          : isOver
            ? 'rgba(249, 182, 18, 0.1)'
            : podColor ? podColor.replace(/[\d.]+\)$/, '0.08)') : 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: crossPodWarning ? '2px solid rgba(255, 0, 0, 0.6)' : 'none',
        transition: 'all 0.2s',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ 
        position: 'sticky',
        top: 0,
        bgcolor: 'rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        zIndex: 1,
        mx: -1.5,
        mt: -1,
        px: 1.5,
        pt: 1,
        pb: 1,
        borderRadius: '8px 8px 0 0'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#C0C0C0', fontSize: '1rem' }}>
            {configuration?.beltNames?.[beltNumber] || `Belt ${beltNumber}`}
          </Typography>
          {podName && (
            <Typography variant="caption" sx={{ color: podColor || 'rgba(255,255,255,0.5)', fontWeight: 600, fontSize: '0.7rem', letterSpacing: '0.5px' }}>
              {podName}
            </Typography>
          )}
          <Chip
            label={`${flights.length}`}
            size="small"
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              color: '#FFFFFF',
              fontWeight: 600,
              minWidth: 30
            }}
          />
        </Box>
        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />
        
        {/* Open/Close Times */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          px: 1, 
          py: 0.5,
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'rgba(255, 255, 255, 0.05)'
          }
        }}
        onClick={onToggleClosed}
        >
          {schedule?.closed ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: '#FF5252'
              }} />
              <Typography variant="caption" sx={{ color: '#FF5252', fontWeight: 600 }}>
                CLOSED
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#4CAF50'
                }} />
                <Typography variant="caption" sx={{ color: '#4CAF50', fontWeight: 500 }}>
                  {schedule?.openTime || '03:00'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#FFA500'
                }} />
                <Typography variant="caption" sx={{ color: '#FFA500', fontWeight: 500 }}>
                  {schedule?.closeTime || '23:00'}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>
      
      <Box sx={{ flexGrow: 1, pt: 1 }}>
        {flights.map((flight) => (
          <FlightTile
            key={`${flight.flightNum}-${flight.depTime}`}
            flight={flight}
            beltNumber={beltNumber}
            highlight={searchTerm && flight.flightNum.includes(searchTerm)}
          />
        ))}
        {flights.length === 0 && (
          <Box sx={{ 
            textAlign: 'center', 
            py: 4,
            color: 'rgba(255, 255, 255, 0.3)'
          }}>
            <Typography variant="caption">
              Drop flights here
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

const BeltGrid = ({ searchTerm = '' }) => {
  const { beltAssignments, configuration, updateConfiguration } = usePiers();
  const { activeBelts, beltSchedule, beltPairs } = configuration;
  const [selectedBelt, setSelectedBelt] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const beltGroups = computeBeltGroups(activeBelts, beltPairs);

  // Map belt number to pod color and name from exportGroups
  const getPodInfo = (beltNum) => {
    const groups = configuration.exportGroups;
    if (!groups || groups.length <= 1) return { color: null, name: null };
    for (const group of groups) {
      const [start, end] = group.beltRange;
      if (beltNum >= start && beltNum <= end) {
        const hex = group.color.slice(2);
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return { color: `rgba(${r}, ${g}, ${b}, 0.5)`, name: group.name };
      }
    }
    return { color: null, name: null };
  };

  const handleOpenDialog = (beltNumber) => {
    setSelectedBelt(beltNumber);
    setDialogOpen(true);
  };

  const handleSaveSchedule = (beltNumber, schedule) => {
    // Create a completely new object to ensure no reference issues
    const newSchedule = {};

    // Copy existing schedules
    Object.keys(beltSchedule).forEach(key => {
      newSchedule[key] = { ...beltSchedule[key] };
    });

    if (schedule) {
      // Add or update the schedule for this specific belt
      newSchedule[String(beltNumber)] = { ...schedule };
    } else {
      // Remove the schedule for this belt
      delete newSchedule[String(beltNumber)];
    }

    updateConfiguration({ beltSchedule: newSchedule });
    // Re-optimization will trigger automatically through PiersView useEffect
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{
        display: 'flex',
        gap: 0,
        height: '100%',
        width: '100%',
        overflowX: 'auto',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '12px',
          height: '12px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(249, 182, 18, 0.3)',
          borderRadius: '6px',
          '&:hover': {
            background: 'rgba(249, 182, 18, 0.5)',
          }
        },
        '&::-webkit-scrollbar-corner': {
          background: 'transparent',
        }
      }}>
        {Array.from({ length: activeBelts }, (_, i) => i + 1).map((beltNumber) => {
          const groupInfo = beltGroups[beltNumber];
          const podInfo = getPodInfo(beltNumber);
          return (
            <Box
              key={beltNumber}
              sx={{
                height: '100%',
                flexShrink: 0,
                mr: groupInfo?.isLast ? 3 : 0.5
              }}
            >
              <BeltColumn
                beltNumber={beltNumber}
                flights={beltAssignments[beltNumber] || []}
                onToggleClosed={() => handleOpenDialog(beltNumber)}
                schedule={beltSchedule[String(beltNumber)]}
                configuration={configuration}
                groupInfo={groupInfo}
                podColor={podInfo.color}
                podName={podInfo.name}
                searchTerm={searchTerm}
              />
            </Box>
          );
        })}
      </Box>
      
      <BeltTimeDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        beltNumber={selectedBelt}
        beltName={selectedBelt ? (configuration.beltNames?.[selectedBelt] || `Belt ${selectedBelt}`) : null}
        currentSchedule={selectedBelt ? beltSchedule[String(selectedBelt)] : null}
        onSave={handleSaveSchedule}
      />
    </DndProvider>
  );
};

export default BeltGrid;